import { ClientSpecMapService } from '../sharedServices';

import { ManifestLoadingTracker } from './ManifestLoadingTracker';
import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../errors';
import {
  IManifestStoreEntity,
  ManifestComponentExtensionType,
  ManifestComponentType,
  ManifestTypeByExtensionType,
  MetadataByExtensionType,
} from './types';

export abstract class AbstractManifestStore<
  TExtensionType extends ManifestComponentExtensionType = ManifestComponentExtensionType,
> {
  #store: Map<ManifestComponentType, IManifestStoreEntity<TExtensionType>>;

  constructor(
    initialStore?: Map<
      ManifestComponentType,
      IManifestStoreEntity<TExtensionType>
    >,
  ) {
    this.#store = new Map<
      ManifestComponentType,
      IManifestStoreEntity<TExtensionType>
    >(initialStore);
  }

  getAll() {
    return Object.fromEntries(this.#store.entries());
  }

  getManifest(compType: string) {
    return this.get(compType)?.manifest;
  }

  getMetadata(compType: string) {
    return this.get(compType)?.metadata ?? null;
  }

  getExtensionType(compType: string) {
    return this.get(compType)?.extensionType ?? null;
  }

  has(compType: string) {
    return this.#store.has(compType);
  }

  get(compType: string, options?: { allowPending: boolean }) {
    const component = this.#store.get(compType);

    if (component?.isPending && !options?.allowPending) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.PendingAppManifest,
        'cannot use the manifest of a pending app. The application must be installed first',
      ).withComponentType(compType);
    }

    return component;
  }

  setManifest(
    compType: string,
    extensionType: TExtensionType,
    manifest: ManifestTypeByExtensionType[TExtensionType],
    metadata: MetadataByExtensionType[TExtensionType],
    isPending?: boolean,
  ) {
    this.#store.set(compType, {
      extensionType,
      componentType: compType,
      manifest,
      metadata,
      isPending: isPending ?? false,
    });

    return manifest;
  }

  isPendingManifest(compType: string) {
    return this.#store.get(compType)?.isPending ?? false;
  }
}
